/**
 * Systemskizze der Schwarz-Weiss-Anlage (Personenschleuse).
 *
 * Reine Funktion: Parameter hinein, `Zeichnung` heraus. Kein DOM, kein jsPDF.
 *
 * ═══ WAS DIE SKIZZE AUSSAGEN MUSS ═════════════════════════════════════════
 * Die Schwarz-Weiss-Anlage ist die einzige Stelle, an der Personen den
 * kontaminierten Bereich verlassen — und die Reihenfolge der Kammern ist ihr
 * ganzer Sinn: kontaminierte PSA ablegen, reinigen, saubere Kleidung anlegen.
 * Wird sie verwechselt, traegt die Schleuse die Kontamination nach draussen,
 * statt sie zurueckzuhalten.
 *
 * Deshalb prueft der Test drei Dinge namentlich:
 *   1. die Kammern stehen in der Reihenfolge schwarz → grau → weiss,
 *   2. der Durchgangspfeil zeigt AUS dem Schwarzbereich HERAUS,
 *   3. die eingezeichnete Gesamtlaenge stimmt mit der Summe der Kammern.
 *
 * Der zweite Punkt ist kein Schoenheitsfehler: ein Pfeil in die Gegenrichtung
 * waere eine gedruckte Anleitung zur Verschleppung.
 *
 * ═══ WARUM DIE ANLAGE PARAMETRISCH IST ════════════════════════════════════
 * Der Regelfall ist die Dreikammeranlage. Es gibt aber Baustellen mit einer
 * einfachen Zweikammerloesung (Grauzone entfaellt, wenn nur Fussschutz und
 * Handschuhe abzulegen sind) und solche mit vier Kammern (getrennte Dusche).
 * Eine fest gezeichnete Skizze zwaenge den Anwender, entweder zu luegen oder
 * die Skizze wegzulassen.
 *
 * ═══ WARUM DIE MASSE ABSCHALTBAR SIND ═════════════════════════════════════
 * Die Anlage selbst ist der technische Regelfall — sie gehoert auch dann in
 * den Plan, wenn niemand sie erfasst hat, denn ohne sie fehlt die Aussage,
 * ueber die der kontaminierte Bereich verlassen wird. Ihre MASSE sind aber
 * eine oertliche Festlegung. Gedruckte „2 m, 2 m, 2 m, 6 m, 3 m" aus der
 * Vorbelegung saehen aus wie eine Planung, die niemand getroffen hat — genau
 * der Grund, aus dem der Zonenplan ohne Erfassung gar nicht gedruckt wird.
 * Deshalb: Schema ja, Zahlen nur, wenn sie erfasst sind
 * (`SchleusenDarstellung.masse`).
 */

import type { SchleusenParameter } from '../types';
import type { Farbrolle, Primitive, Zeichnung } from './primitive';
import { kasten, summe } from './primitive';

/** Vorbelegung nach dem Regelfall: Dreikammeranlage, rund 3 × 6 m. */
export const SCHLEUSE_STANDARD: SchleusenParameter = {
    kammern: [
        { name: 'Kammer 1', art: 'schwarz', laenge_m: 2 },
        { name: 'Kammer 2', art: 'grau', laenge_m: 2 },
        { name: 'Kammer 3', art: 'weiss', laenge_m: 2 },
    ],
    breite_m: 3,
    richtung: 'schwarz_nach_weiss',
    angrenzend_links: 'Rasterfeld (Schwarzbereich)',
    ausstattung: [
        'Sammelbehälter für gebrauchte Einweg-PSA',
        'Stiefelwaschanlage',
        'Waschgelegenheit Hände und Gesicht',
        'Dusche',
    ],
};

/** Was in welcher Kammer geschieht — die Beschriftung, die die Skizze erst
 *  brauchbar macht. Eine Kammer ohne Taetigkeit ist ein Kasten. */
const KAMMER_TEXT: Record<'schwarz' | 'grau' | 'weiss', { rolle: string; tun: string }> = {
    schwarz: { rolle: 'schwarz', tun: 'kontaminierte PSA ablegen' },
    grau: { rolle: 'grau', tun: 'Reinigung, Stiefelwäsche' },
    weiss: { rolle: 'weiss', tun: 'saubere Kleidung anlegen' },
};

const FUELLUNG: Record<'schwarz' | 'grau' | 'weiss', Farbrolle> = {
    schwarz: 'schwarzbereich',
    grau: 'graubereich',
    weiss: 'weissbereich',
};

const TEXTFARBE: Record<'schwarz' | 'grau' | 'weiss', Farbrolle> = {
    schwarz: 'text_hell',
    grau: 'text',
    weiss: 'text',
};

/** Massstab: Millimeter Zeichnung je Meter Wirklichkeit. */
const MM_JE_M = 22;

/** Rand um die Zeichenflaeche in mm. */
const RAND = 14;

/**
 * Darstellungsentscheidungen, die NICHT aus den Parametern folgen.
 */
export interface SchleusenDarstellung {
    /**
     * Masszahlen zeichnen. Vorbelegt mit `true` — wer erfasste Parameter
     * zeichnet, will sie bemasst sehen.
     *
     * `false` fuer den Fall, dass fuer DIESE Baustelle keine Anlage erfasst
     * ist und nur die Vorbelegung gezeichnet wird. Die Abfolge
     * schwarz → grau → weiss ist dann trotzdem richtig: sie ist der
     * technische Regelfall und keine Erfindung. Die Kammerlaengen und die
     * Anlagenbreite sind es sehr wohl — eine bemasste Skizze liest sich als
     * Planung, nicht als Schema. Deshalb faellt in diesem Fall die Bemassung
     * weg und die Fussnote sagt an, dass noch nichts festgelegt ist
     * (Regel 9: der Plan zeigt auch das Offene).
     */
    masse?: boolean;
}

/**
 * Baut die Zeichnung der Schwarz-Weiss-Anlage.
 *
 * Aufbau von links nach rechts:
 *   [ angrenzender Schwarzbereich ] [ Kammer 1 ][ Kammer 2 ][ Kammer 3 ] → Weissbereich
 *
 * Der angrenzende Bereich wird MITGEZEICHNET und nicht nur beschriftet: erst
 * durch ihn wird sichtbar, dass die Anlage an der Grenze steht und nicht
 * irgendwo daneben.
 */
export const zeichneSchleuse = (
    p: SchleusenParameter,
    darstellung: SchleusenDarstellung = {},
): Zeichnung => {
    const mitMassen = darstellung.masse !== false;
    const kammern = p.kammern.length > 0 ? p.kammern : SCHLEUSE_STANDARD.kammern;
    const breiteM = p.breite_m > 0 ? p.breite_m : SCHLEUSE_STANDARD.breite_m;

    const feldB = 42;                                   // angrenzender Bereich
    const kammerB = kammern.map(k => Math.max(12, k.laenge_m * MM_JE_M));
    const anlageB = summe(kammerB);
    const hoeheK = Math.max(24, breiteM * MM_JE_M);

    const x0 = RAND;
    const yTop = RAND + 12;                             // Platz fuer den Pfeil
    const anlageX = x0 + feldB;

    const teile: Primitive[] = [];

    // ── angrenzender Schwarzbereich ────────────────────────────────────────
    teile.push(...kasten(x0, yTop, feldB, hoeheK, 'schwarzbereich', [
        { text: p.angrenzend_links || 'Schwarzbereich', groesse: 8, fett: true, farbe: 'text_hell' },
    ]));

    // ── die Kammern ────────────────────────────────────────────────────────
    let x = anlageX;
    kammern.forEach((k, i) => {
        const b = kammerB[i];
        const t = KAMMER_TEXT[k.art];
        teile.push(...kasten(x, yTop, b, hoeheK, FUELLUNG[k.art], [
            { text: k.name, groesse: 8, fett: true, farbe: TEXTFARBE[k.art] },
            { text: t.rolle, groesse: 6.5, farbe: TEXTFARBE[k.art] },
            { text: t.tun, groesse: 6, farbe: TEXTFARBE[k.art] },
        ]));
        x += b;
    });

    const anlageEnde = anlageX + anlageB;

    // ── Grenze Schwarz/Weiss ───────────────────────────────────────────────
    // Sie liegt am AUSSENRAND des Rasterfeldes, also am Beginn der Anlage.
    teile.push({
        art: 'linie',
        x1: anlageX, y1: yTop - 6, x2: anlageX, y2: yTop + hoeheK + 8,
        linie: 'warnung', staerke: 0.7, strich: 'gestrichelt',
    });
    teile.push({
        art: 'text', x: anlageX, y: yTop + hoeheK + 12,
        text: 'Grenze Schwarz / Weiß', groesse: 6.5, anker: 'mitte', farbe: 'warnung',
    });

    // ── Durchgangsrichtung ─────────────────────────────────────────────────
    // IMMER aus dem Schwarzbereich heraus. `richtung` ist im Typ auf genau
    // diesen einen Wert festgelegt; die Zeichnung haelt sich daran, ohne ihn
    // abzufragen — es gibt keine zweite Moeglichkeit.
    teile.push({
        art: 'pfeil',
        x1: anlageX + 4, y1: yTop - 5,
        x2: anlageEnde - 2, y2: yTop - 5,
        linie: 'warnung', staerke: 0.8, spitze: 4,
    });
    teile.push({
        art: 'text', x: (anlageX + anlageEnde) / 2, y: yTop - 7.5,
        text: 'Personendurchgang schwarz nach weiß', groesse: 7, fett: true,
        anker: 'mitte', farbe: 'warnung',
    });

    // ── Weissbereich rechts ────────────────────────────────────────────────
    teile.push({
        art: 'text', x: anlageEnde + 4, y: yTop + 6,
        text: 'Weißbereich', groesse: 8, fett: true, anker: 'links', farbe: 'text',
    });
    teile.push({
        art: 'text', x: anlageEnde + 4, y: yTop + 11,
        text: '(unbelastet)', groesse: 6.5, anker: 'links', farbe: 'linie',
    });

    // ── Masse ──────────────────────────────────────────────────────────────
    // Nur, wenn sie jemand fuer diese Baustelle festgelegt hat. Siehe
    // `SchleusenDarstellung`.
    if (mitMassen) {
        let mx = anlageX;
        kammern.forEach((k, i) => {
            teile.push({
                art: 'masslinie',
                x1: mx, y1: yTop + hoeheK + 18, x2: mx + kammerB[i], y2: yTop + hoeheK + 18,
                text: `${zahl(k.laenge_m)} m`,
            });
            mx += kammerB[i];
        });
        teile.push({
            art: 'masslinie',
            x1: anlageX, y1: yTop + hoeheK + 26, x2: anlageEnde, y2: yTop + hoeheK + 26,
            text: `${zahl(summe(kammern.map(k => k.laenge_m)))} m`,
        });
        teile.push({
            art: 'masslinie',
            x1: anlageEnde + 30, y1: yTop, x2: anlageEnde + 30, y2: yTop + hoeheK,
            text: `${zahl(breiteM)} m`,
        });
    }

    // ── Ausstattung ────────────────────────────────────────────────────────
    const ausstattung = p.ausstattung ?? [];
    let ay = yTop + hoeheK + (mitMassen ? 36 : 20);
    if (ausstattung.length > 0) {
        teile.push({
            art: 'text', x: x0, y: ay,
            text: mitMassen ? 'Ausstattung:' : 'Ausstattung im Regelfall:',
            groesse: 7, fett: true, anker: 'links', farbe: 'text',
        });
        ay += 4.5;
        for (const a of ausstattung) {
            teile.push({
                art: 'text', x: x0 + 3, y: ay, text: `• ${a}`,
                groesse: 6.5, anker: 'links', farbe: 'text',
            });
            ay += 4;
        }
    }

    return {
        breite: anlageEnde + 44,
        hoehe: ay + RAND,
        titel: 'Systemskizze — Schwarz-Weiß-Anlage',
        untertitel: mitMassen
            ? `${kammern.length}-Kammer-Personenschleuse am Rand des kontaminierten Bereichs`
            : 'Regelausführung einer Personenschleuse am Rand des kontaminierten Bereichs '
                + '— für diese Baustelle noch nicht festgelegt',
        teile,
        legende: [
            { farbe: 'schwarzbereich', text: 'Schwarzbereich — kontaminiert' },
            { farbe: 'graubereich', text: 'Uebergangs-/Graubereich — Reinigung' },
            { farbe: 'weissbereich', text: 'Weißbereich — unbelastet' },
        ],
        fussnoten: [
            ...(mitMassen ? [] : [
                'Schematische Darstellung ohne Maße: Für diese Baustelle ist noch keine '
                + 'Schwarz-Weiß-Anlage erfasst. Die dargestellte Abfolge schwarz, grau, '
                + 'weiß ist die Regelausführung; Kammerzahl, Abmessungen und Ausstattung '
                + 'sind vor Einrichtung der Anlage festzulegen und hier nachzutragen.',
            ]),
            'Der Aufstellort je Bereich wird von der ausführenden Firma festgelegt; der '
            + 'Koordinator nach DGUV Regel 101-004 stimmt die Lage der Bereichsgrenzen mit ab.',
            'Die Anlage dient ausschließlich dem Kleidungswechsel und der Körperreinigung — '
            + 'nicht als Pausenraum und nicht zur Aufbewahrung von Speisen oder '
            + 'Freizeitkleidung.',
        ],
    };
};

/** Deutsche Zahlendarstellung ohne Nachkommastelle bei glatten Werten. */
const zahl = (n: number): string =>
    (Number.isInteger(n) ? String(n) : n.toFixed(1)).replace('.', ',');

// ═══════════════════════════════════════════════════════════════════════════
// PRUEFUNG DER PARAMETER
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Prueft die Schleusenparameter auf fachliche Fehler.
 *
 * Getrennt von der Zeichenfunktion: eine Skizze soll auch dann entstehen,
 * wenn etwas nicht stimmt — sonst sieht der Anwender nicht, WAS nicht
 * stimmt. Die Zeichnung zeigt den Zustand, diese Funktion sagt ihn an.
 */
export const pruefeSchleuse = (p: SchleusenParameter): string[] => {
    const fehler: string[] = [];
    const arten = p.kammern.map(k => k.art);

    if (p.kammern.length < 2) {
        fehler.push('Eine Personenschleuse braucht mindestens zwei Kammern: eine zum Ablegen '
            + 'der kontaminierten PSA und eine zum Anlegen sauberer Kleidung.');
    }
    if (arten[0] !== 'schwarz') {
        fehler.push('Die erste Kammer muss dem Schwarzbereich zugewandt sein — dort wird die '
            + 'kontaminierte PSA abgelegt.');
    }
    if (arten[arten.length - 1] !== 'weiss') {
        fehler.push('Die letzte Kammer muss der Weißbereich sein — dort wird saubere Kleidung '
            + 'angelegt.');
    }

    // Die Reinheit darf auf dem Weg nach draussen nicht zurueckfallen.
    const rang = { schwarz: 0, grau: 1, weiss: 2 };
    for (let i = 1; i < arten.length; i++) {
        if (rang[arten[i]] < rang[arten[i - 1]]) {
            fehler.push(`Kammer ${i + 1} (${arten[i]}) ist unreiner als die vorhergehende `
                + `(${arten[i - 1]}). Auf dem Weg nach draußen darf die Reinheit nicht `
                + 'zurückfallen.');
            break;
        }
    }

    if (p.breite_m <= 0) fehler.push('Breite der Anlage angeben.');
    if (p.kammern.some(k => k.laenge_m <= 0)) fehler.push('Jede Kammer braucht eine Länge.');

    return fehler;
};
